import CampaignStatus from '@/Enums/CampaignManagement/CampaignStatus.enum';
import type { IDV360TicketsLineItem } from '@/Interfaces/DV360Tickets/LineItem.interface';
import { getLineItemActivationConditions } from '@/helpers/campaignManagement/lineItemStatus.helpers';
import LineItemActivationButton from '@/pages/CampaignManagement/components/LineItemActivationButton';
import { useAppDispatch } from '@/store/hooks';
import { updateDV360TicketsLineItem } from '@/store/slices/dv360Tickets/insertionOrderManagement.slice';
import * as Sentry from '@sentry/react';
import { memo, useMemo } from 'react';
import { toast } from 'react-toastify';

interface IProps {
  activeLineItem?: IDV360TicketsLineItem;
  setLoading: (flag: boolean) => void;
  loading: boolean;
  readOnly?: boolean;
}

const DV360LineItemStatusChangeButton = memo(({ activeLineItem, loading, setLoading, readOnly }: IProps) => {
  const dispatch = useAppDispatch();
  const status = activeLineItem?.status ?? CampaignStatus.INACTIVE;
  const isActive = status === CampaignStatus.ACTIVE;

  const conditions = useMemo(
    () =>
      getLineItemActivationConditions({
        id: activeLineItem?.id,
        creativesCount: activeLineItem?.creatives?.length ?? 0,
      }),
    [activeLineItem?.id, activeLineItem?.creatives],
  );

  const onToggle = async () => {
    if (!activeLineItem?.id) return;

    setLoading(true);
    try {
      await dispatch(
        updateDV360TicketsLineItem({
          id: activeLineItem.id,
          status: isActive ? CampaignStatus.INACTIVE : CampaignStatus.ACTIVE,
        }),
      ).unwrap();
      toast.success('Line Item status updated');
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to update Line Item status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LineItemActivationButton
      isActive={isActive}
      conditions={conditions}
      onToggle={onToggle}
      disabled={loading || readOnly}
    />
  );
});

export default DV360LineItemStatusChangeButton;
